import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function utcDate(value) {
  const clean = String(value || "").slice(0, 10);
  return new Date(`${clean}T00:00:00Z`);
}

function hexTextColor(hex) {
  const value = String(hex || "#FFFFFF").replace("#", "");
  const luminance = (parseInt(value.slice(0, 2), 16) * 299 + parseInt(value.slice(2, 4), 16) * 587 + parseInt(value.slice(4, 6), 16) * 114) / 1000;
  return luminance < 145 ? "#FFFFFF" : "#18221B";
}

async function imageToDataUrl(url) {
  if (!url) return null;
  if (/^data:image\/(png|jpe?g);base64,/i.test(url)) return url;
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
}

function monthSequence(startDate) {
  const start = utcDate(startDate);
  return Array.from({ length: 12 }, (_, index) => new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + index, 1)));
}

function eventsForMonth(events, month) {
  const monthStart = month.getTime();
  const monthEnd = Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0);
  return events.filter((event) => utcDate(event.startDate).getTime() <= monthEnd && utcDate(event.endDate).getTime() >= monthStart);
}

function eventOnDay(event, day) {
  const time = day.getTime();
  return utcDate(event.startDate).getTime() <= time && utcDate(event.endDate).getTime() >= time;
}

function dateLabel(event) {
  const start = utcDate(event.startDate);
  const end = utcDate(event.endDate);
  const first = `${SHORT_MONTHS[start.getUTCMonth()]} ${start.getUTCDate()}`;
  if (start.getTime() === end.getTime()) return first;
  const last = start.getUTCMonth() === end.getUTCMonth() ? String(end.getUTCDate()) : `${SHORT_MONTHS[end.getUTCMonth()]} ${end.getUTCDate()}`;
  return `${first}-${last}`;
}

function fullDateLabel(value) {
  const date = utcDate(value);
  return `${SHORT_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

function scheduleDateLabel(event) {
  if (event.startDate === event.endDate) return fullDateLabel(event.startDate);
  return `${fullDateLabel(event.startDate)} – ${fullDateLabel(event.endDate)}`;
}

function scheduleDayLabel(event) {
  const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const start = weekdays[utcDate(event.startDate).getUTCDay()];
  const end = weekdays[utcDate(event.endDate).getUTCDay()];
  return event.startDate === event.endDate ? start : `${start} – ${end}`;
}

function documentHeader(logo, businessName, title, yearName, compact = false) {
  const logoSize = compact ? 34 : 42;
  return {
    columns: [
      logo ? { image: logo, width: logoSize, height: logoSize, fit: [logoSize, logoSize] } : { width: logoSize, text: "" },
      { width: "*", stack: [
        { text: businessName, alignment: "center", bold: true, fontSize: compact ? 14 : 16, color: "#244B31" },
        { text: `${title} Calendar`, alignment: "center", bold: true, fontSize: compact ? 10 : 12, color: "#2F7D4A", margin: [0, 2, 0, 0] },
        { text: yearName, alignment: "center", fontSize: compact ? 8.5 : 10, color: "#617065", margin: [0, 1, 0, 0] },
      ] },
      { width: logoSize, text: "" },
    ],
    margin: [0, 0, 0, compact ? 9 : 5],
  };
}

function schedulePage(events, logo, businessName, title, yearName) {
  const orderedEvents = [...events].sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)) || String(a.title).localeCompare(String(b.title)));
  const dense = orderedEvents.length > 28;
  const body = [
    [
      { text: "Date", bold: true, alignment: "center", colSpan: 1 },
      { text: "Day", bold: true, alignment: "center" },
      { text: "Occasion", bold: true, alignment: "center", colSpan: 2 },
      {},
    ],
    ...orderedEvents.map((event) => [
      { text: scheduleDateLabel(event), alignment: "center" },
      { text: scheduleDayLabel(event), alignment: "center" },
      { text: "", fillColor: event.CalendarOccasion && event.CalendarOccasion.color || "#DDE3DE" },
      { text: event.title, alignment: "left" },
    ]),
  ];
  if (!orderedEvents.length) body.push([{ text: "No calendar entries", colSpan: 4, alignment: "center", italics: true, color: "#89938C", margin: [0, 10, 0, 10] }, {}, {}, {}]);
  return [
    { ...documentHeader(logo, businessName, title, yearName, true), pageBreak: "before" },
    { text: "Calendar Dates and Occasions", alignment: "center", bold: true, fontSize: 11, color: "#263B2C", margin: [0, 0, 0, 8] },
    {
      table: { headerRows: 1, widths: [126, 112, 13, "*"], body },
      layout: {
        hLineWidth: (index, node) => index === 0 || index === node.table.body.length ? 1 : 0.4,
        vLineWidth: (index) => index === 0 || index === 4 ? 1 : 0.4,
        hLineColor: (index, node) => index === 0 || index === node.table.body.length ? "#202820" : "#CDD2CE",
        vLineColor: (index) => index === 0 || index === 4 ? "#202820" : "#CDD2CE",
        fillColor: (rowIndex) => rowIndex === 0 ? "#F2F4F2" : null,
        paddingLeft: () => dense ? 3 : 5,
        paddingRight: () => dense ? 3 : 5,
        paddingTop: () => dense ? 2.2 : 4.5,
        paddingBottom: () => dense ? 2.2 : 4.5,
      },
      fontSize: dense ? 6.2 : 7.4,
    },
  ];
}

function monthBlock(month, allEvents, openWeekdays, cellPadding) {
  const year = month.getUTCFullYear();
  const monthIndex = month.getUTCMonth();
  const firstWeekday = new Date(Date.UTC(year, monthIndex, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const monthEvents = eventsForMonth(allEvents, month);
  const dayRows = [];
  let dayNumber = 1;
  const weekCount = Math.ceil((firstWeekday + daysInMonth) / 7);
  for (let week = 0; week < weekCount; week += 1) {
    const row = [];
    for (let weekday = 0; weekday < 7; weekday += 1) {
      if ((week === 0 && weekday < firstWeekday) || dayNumber > daysInMonth) {
        row.push({ text: "", fillColor: "#FFFFFF" });
      } else {
        const day = new Date(Date.UTC(year, monthIndex, dayNumber));
        const matching = monthEvents.filter((event) => eventOnDay(event, day));
        const occasion = matching[0] && matching[0].CalendarOccasion;
        const isClosed = !openWeekdays.includes(weekday);
        const isHighlighted = Boolean(occasion) || isClosed;
        const fillColor = occasion ? occasion.color : "#FFFFFF";
        row.push({ text: String(dayNumber), alignment: "center", bold: isHighlighted, fontSize: isHighlighted ? 8.4 : 7.2, characterSpacing: isHighlighted ? 0.1 : 0, color: occasion ? hexTextColor(fillColor) : (isClosed ? "#C62828" : "#28342C"), fillColor });
        dayNumber += 1;
      }
    }
    dayRows.push(row);
  }
  const noteFontSize = monthEvents.length >= 7 ? 4.5 : monthEvents.length >= 5 ? 4.8 : 5.2;
  const notes = monthEvents.length ? monthEvents.map((event) => ({ text: [{ text: "■ ", color: event.CalendarOccasion.color }, { text: `${dateLabel(event)}  `, bold: true, color: "#2C3A30" }, { text: event.title, color: "#3E4A42" }], fontSize: noteFontSize, lineHeight: 1, margin: [1, 0.7, 0, 0] })) : [{ text: "No scheduled events", color: "#A0AAA2", italics: true, fontSize: 5.2, margin: [1, 2, 0, 0] }];
  const monthTitleRow = [{ text: `${MONTHS[monthIndex]} ${year}`, colSpan: 7, alignment: "center", bold: true, fontSize: 9, color: "#294532", fillColor: "#D9EBCF", margin: [0, 1.5, 0, 1.5] }, ...Array.from({ length: 6 }, () => ({}))];
  const weekdayRow = ["S", "M", "T", "W", "T", "F", "S"].map((text, index) => { const isClosed = !openWeekdays.includes(index); return { text, alignment: "center", bold: true, fontSize: 7.2, color: isClosed ? "#FFFFFF" : "#273129", fillColor: isClosed ? "#C51627" : "#F0F2F0" }; });
  return {
    stack: [
      { table: { widths: Array(7).fill("*"), body: [monthTitleRow, weekdayRow, ...dayRows] }, layout: { hLineWidth: (index, node) => index === 0 || index === node.table.body.length ? 1 : 0.35, vLineWidth: (index) => index === 0 || index === 7 ? 1 : 0.35, hLineColor: (index, node) => index === 0 || index === node.table.body.length ? "#202820" : "#C9CFCB", vLineColor: (index) => index === 0 || index === 7 ? "#202820" : "#C9CFCB", paddingLeft: () => 1, paddingRight: () => 1, paddingTop: (row) => row === 0 ? 2.2 : cellPadding, paddingBottom: (row) => row === 0 ? 2.2 : cellPadding } },
      { stack: notes, margin: [0, 4, 0, 2] },
    ],
    margin: [2, 3, 2, 7],
  };
}

export async function buildCalendarPdfDefinition({ year, events, business }) {
  const logo = await imageToDataUrl(business && (business.logoDataUrl || business.imageUrl));
  const months = monthSequence(year.startDate);
  const configuredDays = year.Class && year.Class.metadata && year.Class.metadata.openWeekdays;
  const openWeekdays = Array.isArray(configuredDays) ? configuredDays.map(Number) : [1, 2, 3, 4, 5];
  const busiestMonth = Math.max(...months.map((month) => eventsForMonth(events, month).length), 0);
  const cellPadding = busiestMonth >= 5 ? 3.2 : busiestMonth >= 3 ? 3.8 : 4.4;
  const grid = [];
  for (let row = 0; row < 4; row += 1) grid.push(months.slice(row * 3, row * 3 + 3).map((month) => monthBlock(month, events, openWeekdays, cellPadding)));
  const title = year.Class && year.Class.name ? year.Class.name : "Academic";
  const businessName = (business && business.name) || "Muhammadi Masjid";
  const content = [
    documentHeader(logo, businessName, title, year.name),
    { table: { widths: ["*", "*", "*"], body: grid }, layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 5, paddingRight: () => 5, paddingTop: () => 1, paddingBottom: () => 1 } },
    ...schedulePage(events, logo, businessName, title, year.name),
  ];
  return {
    pageSize: "LETTER",
    pageOrientation: "portrait",
    pageMargins: [22, 18, 22, 10],
    content,
    defaultStyle: { font: "Roboto", fontSize: 6.5 },
    info: { title: `${title} Calendar ${year.name}`, subject: "Academic calendar", author: businessName },
  };
}

export function calendarPdf(definition) {
  return pdfMake.createPdf(definition);
}
